'use client';

import { memo, useCallback } from 'react';

import { useTranslations } from 'next-intl';

import { ChooseAvatarDialog } from '@/app/[locale]/(main)/profile/edit/_components/ChooseAvatar';
import PenIcon from '@/assets/icons/pen.svg';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { Button } from '@/components/ui/button';
import { I_Avatar, I_AvatarTabs } from '@/data/editProfileMock';

interface I_AvatarSectionProps {
  avatarUrl: string | null;
  isUserLoading: boolean;
  isUpdatingAvatar: boolean;
  avatars: I_AvatarTabs | null;
  onAvatarUpdate: (avatar: I_Avatar) => void;
  onToggleChooseAvatar: () => void;
  isOpenChooseAvatar: boolean;
  onCloseChooseAvatar: () => void;
}

const AvatarSection = memo(function AvatarSection({
  avatarUrl,
  isUserLoading,
  isUpdatingAvatar,
  avatars,
  onAvatarUpdate,
  onToggleChooseAvatar,
  isOpenChooseAvatar,
  onCloseChooseAvatar
}: I_AvatarSectionProps) {
  const t = useTranslations('editProfile');

  const handleAvatarClick = useCallback(() => {
    if (!isUpdatingAvatar) {
      onToggleChooseAvatar();
    }
  }, [isUpdatingAvatar, onToggleChooseAvatar]);

  return (
    <>
      <div
        className={`relative w-24 h-24 flex justify-end mx-auto ${
          isUpdatingAvatar ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`}
        onClick={handleAvatarClick}
      >
        {isUserLoading || isUpdatingAvatar ? (
          <div className="w-24 h-24 rounded-full bg-background flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-form-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <UserAvatar
            key={avatarUrl}
            src={avatarUrl}
            alt={t('accessibility.avatar')}
            width={96}
            height={96}
            className="rounded-full border-2 w-full h-full"
          />
        )}
        <Button
          variant={'secondary'}
          icon={<PenIcon />}
          className={`absolute rounded-full w-9 h-9 p-0 z-1 -mr-4 ${
            isUpdatingAvatar ? 'opacity-50' : ''
          }`}
          disabled={isUpdatingAvatar}
        />
      </div>

      <ChooseAvatarDialog
        open={isOpenChooseAvatar}
        onOpenChange={onCloseChooseAvatar}
        onAvatarSelect={onAvatarUpdate}
        avatars={avatars}
        isUploading={isUpdatingAvatar}
      />
    </>
  );
});

export default AvatarSection;
